import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";

import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { PersonalTrainingData, BusinessTrainingData } from "../Data/CommunityCoursesData";

import "./Training.css";

const courseImageMap = {
  'customer-service': require('../../images/courses/customerservicelevel2-3/customer-service.avif'),
  'health-safety': require('../../images/courses/health-safetytraining/health-safetytraining.jpeg'),
  'food-hygiene': require('../../images/courses/food-hygienetraining/food-hygiene.jpg'),
  'safeguarding': require('../../images/courses/safeguarding/safeguarding-2.jpg'),
  'paediatric-first-aid': require('../../images/courses/paediatricfirstaid/paediatricfirstaid-2.jpg'),
  'business-admin': require('../../images/courses/business-administration/business-administration.jpg'),
  'esol': require('../../images/courses/esolenglishforspeakersofotherlanguages/esolenglishforspeakersofotherlanguages-2.jpg'),
  'functional-skills-english': require('../../images/courses/functionalskillsenglish/photo-1753161020548-941a7ae21cb3.avif'),
  'functional-skills-maths': require('../../images/courses/functionalskillsmaths/functionalskillsmaths.avif'),
  'it-digital-skills': require('../../images/courses/it-digitalskills/it-digitalskills.avif'),
  'healthcare-package': require('../../images/courses/healthcaretrainingpackage/healthcaretrainingpackage-3.jpg'),
  'childcare-package': require('../../images/courses/childcaretrainingpackage/childcare.avif'),
  'admin-package': require('../../images/courses/adminsectortrainingpackage/adminsectortrainingpackage.jpg'),
  'catering-package': require('../../images/courses/cateringsectortrainingpackage/cateringsectortrainingpackage.jpg'),
  'default': require('./training_hero_bg.png'),
};

const getCourseImage = (courseId) => {
  return courseImageMap[courseId] || courseImageMap.default;
};

const ModuleCard = ({ module, index, isExpanded, onToggle }) => (
  <div className={`module-v2 ${isExpanded ? "module-v2--expanded" : ""}`}>
    <button className="module-v2__header" onClick={onToggle}>
      <div className="module-v2__number">{String(index + 1).padStart(2, "0")}</div>
      <div className="module-v2__info">
        <h3 className="module-v2__title">{module.title}</h3>
        <p className="module-v2__desc-preview">{module.description}</p>
      </div>
      <div className="module-v2__toggle">
        <svg 
          width="24" 
          height="24" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          className={isExpanded ? "rotated" : ""}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </button>
    
    {isExpanded && (
      <div className="module-v2__content">
        {module.learningOutcomes && module.learningOutcomes.length > 0 && (
          <div className="module-v2__section">
            <h4 className="module-v2__label">You will be able to</h4>
            <ul className="module-v2__list">
              {module.learningOutcomes.map((outcome, i) => (
                <li key={i}>{outcome}</li>
              ))}
            </ul>
          </div>
        )}
        
        {module.topics && module.topics.length > 0 && (
          <div className="module-v2__section">
            <h4 className="module-v2__label">Topics covered</h4>
            <div className="module-v2__tags">
              {module.topics.map((topic, i) => (
                <span key={i} className="module-v2__tag">{topic}</span>
              ))}
            </div>
          </div>
        )}
        
        {module.assessmentCriteria && module.assessmentCriteria.length > 0 && (
          <div className="module-v2__section">
            <h4 className="module-v2__label">How you'll be assessed</h4>
            <ul className="module-v2__list module-v2__list--subtle">
              {module.assessmentCriteria.map((criteria, i) => (
                <li key={i}>{criteria}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    )}
  </div>
);

const normalizeCourse = (rawCourse, category, isBusiness = false) => {
  const image = getCourseImage(rawCourse.id);
  const imageUrl = typeof image === 'object' && image.default ? image.default : image;
  
  if (isBusiness) {
    const businessModules = rawCourse.features ? rawCourse.features.map((feature, idx) => ({
      id: `module-${idx + 1}`,
      title: feature.replace('✓ ', '').replace(/^Includes? /i, ''),
      description: `Core component of the ${rawCourse.title}`,
      topics: [],
      learningOutcomes: [],
    })) : [];
    
    return {
      id: rawCourse.id,
      name: rawCourse.title,
      shortDescription: rawCourse.shortDescription,
      fullDescription: rawCourse.description,
      level: rawCourse.level || 'Level 2',
      duration: rawCourse.duration,
      accreditation: 'Nationally recognised qualification',
      imagePath: imageUrl,
      category: 'Business Training',
      categoryTitle: 'Business Training Packages',
      modules: businessModules,
      whoThisCourseIsFor: rawCourse.outcomes ? rawCourse.outcomes.map(o => o) : [],
      careerPaths: rawCourse.outcomes ? rawCourse.outcomes.map(o => ({ title: 'Relevant Sector', roles: o })) : [],
      courseBenefits: rawCourse.features || [],
      entryRequirements: ['Minimum group size: ' + (rawCourse.minGroupSize || 5), 'Contact us for eligibility'],
      deliveryMethods: ['On-site delivery', 'Flexible scheduling', 'Group training'],
      assessmentMethods: ['Portfolio of evidence', 'Practical demonstration', 'Knowledge assessment'],
      enrollmentInfo: {
        cost: 'Contact us for pricing',
        schedule: 'Flexible',
        nextIntake: 'Rolling enrolment',
        support: 'Dedicated account manager',
      },
      isBusiness: true,
      bookingInfo: rawCourse.bookingInfo,
    };
  }
  
  return {
    id: rawCourse.id,
    name: rawCourse.name,
    shortDescription: rawCourse.shortDescription,
    fullDescription: rawCourse.fullDescription || rawCourse.description,
    level: rawCourse.level,
    duration: rawCourse.duration,
    accreditation: rawCourse.accreditation,
    imagePath: imageUrl,
    category: category?.title || 'Training',
    categoryTitle: category?.title,
    modules: rawCourse.modules || [],
    whoThisCourseIsFor: rawCourse.whoThisCourseIsFor || [],
    careerPaths: rawCourse.careerPaths || [],
      courseBenefits: rawCourse.courseBenefits || [],
    entryRequirements: rawCourse.entryRequirements || [],
    deliveryMethods: rawCourse.deliveryMethods || [],
    assessmentMethods: rawCourse.assessmentMethods || [],
    progressionOpportunities: rawCourse.progressionOpportunities || [],
    successStory: rawCourse.successStory,
    enrollmentInfo: category?.enrollmentInfo,
    isBusiness: false,
  };
};

const CoursePage = () => {
  const { categoryId, programmeId, packageId } = useParams();
  const [expandedModule, setExpandedModule] = useState(0);
  
  let course = null;
  let category = null;
  
  if (programmeId && categoryId) {
    category = PersonalTrainingData.find((c) => c.id === categoryId);
    const programme = category?.programs?.find((p) => p.id === programmeId);
    if (programme) {
      course = normalizeCourse(programme, category, false);
    }
  } else if (packageId) {
    const businessPackage = BusinessTrainingData.find((p) => p.id === packageId);
    if (businessPackage) {
      course = normalizeCourse(businessPackage, null, true);
    }
  }

  if (!course) {
    return (
      <div className="programme-v2">
        <Navbar />
        <div className="not-found-v2">
          <div className="not-found-v2__icon">404</div>
          <h2>Course not found</h2>
          <p>The training course you're looking for doesn't exist.</p>
          <Link to="/training" className="not-found-v2__link">
            ← Back to Training
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const backLinkPath = course.isBusiness ? "/training/business" : "/training";

  return (
    <div className="programme-v2">
      <Navbar />
      
      <section 
        className="hero-v2 hero-v2--with-image" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)), url(${course.imagePath})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "#ffffff"
        }}
      >
        <div className="hero-v2__container">
          <Link to={backLinkPath} className="hero-v2__back">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 10H5M5 10L10 5M5 10L10 15" />
            </svg>
            All Training
          </Link>

          <div className="hero-v2__badges">
            <span className="hero-v2__badge hero-v2__badge--category">{course.category}</span>
            {course.level && (
              <span className="hero-v2__badge hero-v2__badge--level">{course.level}</span>
            )}
          </div>

          <h1 className="hero-v2__title">{course.name}</h1>
          {course.shortDescription && (
            <p className="hero-v2__tagline">{course.shortDescription}</p>
          )}

          {course.accreditation && (
            <div className="hero-v2__accreditation">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" stroke="currentColor" strokeWidth="2">
                <path d="M10 2l2.5 5 5.5 1-4 4 1 5.5L10 15l-5 2.5 1-5.5-4-4 5.5-1L10 2z" />
              </svg>
              {course.accreditation}
            </div>
          )}
        </div>
      </section>

      <section className="stats-v2">
        <div className="stats-v2__container">
          <div className="stats-v2__item">
            <div className="stats-v2__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
            </div>
            <div className="stats-v2__content">
              <span className="stats-v2__label">Duration</span>
              <span className="stats-v2__value">{course.duration || "Flexible"}</span>
            </div>
          </div>
          
          <div className="stats-v2__divider" />
          
          <div className="stats-v2__item">
            <div className="stats-v2__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <path d="M22 4L12 14.01l-3-3" />
              </svg>
            </div>
            <div className="stats-v2__content">
              <span className="stats-v2__label">Level</span>
              <span className="stats-v2__value">{course.level || "Various"}</span>
            </div>
          </div>
          
          <div className="stats-v2__divider" />
          
          <div className="stats-v2__item">
            <div className="stats-v2__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className="stats-v2__content">
              <span className="stats-v2__label">Cost</span>
              <span className="stats-v2__value">{course.enrollmentInfo?.cost || "Contact us"}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="programme-v2__main">
        <div className="programme-v2__grid">
          
          <div className="programme-v2__content">
            
            {course.fullDescription && (
              <section className="section-v2">
                <h2 className="section-v2__title">Overview</h2>
                <p className="section-v2__text">{course.fullDescription}</p>
              </section>
            )}
            
            {course.whoThisCourseIsFor && course.whoThisCourseIsFor.length > 0 && (
              <section className="section-v2">
                <h2 className="section-v2__title">{course.isBusiness ? 'Outcomes' : 'Who this is for'}</h2>
                <div className="for-v2">
                  {course.whoThisCourseIsFor.map((item, i) => (
                    <div key={i} className="for-v2__item">
                      <div className="for-v2__check">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M13.5 4.5L6 12l-3.5-3.5" />
                        </svg>
                      </div>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            
            {course.modules && course.modules.length > 0 && (
              <section className="section-v2">
                <h2 className="section-v2__title">{course.isBusiness ? 'Package includes' : 'Course content'}</h2>
                <p className="section-v2__subtitle">
                  {course.modules.length} {course.isBusiness ? 'components' : 'modules'} • {course.duration}
                </p>
                <div className="modules-v2">
                  {course.modules.map((module, index) => (
                    <ModuleCard
                      key={module.id || index}
                      module={module}
                      index={index}
                      isExpanded={expandedModule === index}
                      onToggle={() => setExpandedModule(expandedModule === index ? -1 : index)}
                    />
                  ))}
                </div>
              </section>
            )}
            
            {course.entryRequirements && course.entryRequirements.length > 0 && (
              <section className="section-v2">
                <h2 className="section-v2__title">{course.isBusiness ? 'Requirements' : 'Entry requirements'}</h2>
                <ul className="requirements-v2">
                  {course.entryRequirements.map((req, i) => (
                    <li key={i} className="requirements-v2__item">
                      <span className="requirements-v2__bullet">→</span>
                      {req}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            
            {course.assessmentMethods && course.assessmentMethods.length > 0 && !course.isBusiness && (
              <section className="section-v2">
                <h2 className="section-v2__title">How you're assessed</h2>
                <div className="assessment-v2">
                  {course.assessmentMethods.map((method, i) => (
                    <div key={i} className="assessment-v2__item">
                      {method}
                    </div>
                  ))}
                </div>
              </section>
            )}
            
            {course.successStory && (
              <section className="section-v2">
                <div className="story-v2">
                  <div className="story-v2__quote">
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="currentColor">
                      <path d="M12 24c0-6.627 5.373-12 12-12v-8c-11.046 0-20 8.954-20 20h8zm24 0c0-6.627-5.373-12-12-12v-8c11.046 0 20 8.954 20 20h-8z" />
                    </svg>
                    <p>{course.successStory.quote}</p>
                  </div>
                  <div className="story-v2__author">
                    <div className="story-v2__avatar">
                      {course.successStory.name.charAt(0)}
                    </div>
                    <div className="story-v2__info">
                      <span className="story-v2__name">{course.successStory.name}</span>
                      <span className="story-v2__bg">{course.successStory.background}</span>
                    </div>
                  </div>
                  <p className="story-v2__journey">{course.successStory.journey}</p>
                </div>
              </section>
            )}
            
            {course.bookingInfo && (
              <section className="section-v2">
                <h2 className="section-v2__title">Booking Information</h2>
                <p className="section-v2__text">{course.bookingInfo}</p>
              </section>
            )}
            
          </div>
          
          <aside className="programme-v2__sidebar">
            
            <div className="sidebar-v2 sidebar-v2--cta">
              <h3 className="sidebar-v2__title">Ready to start?</h3>
              <p className="sidebar-v2__text">
                {course.isBusiness 
                  ? 'Get in touch to discuss bulk enrolment and tailored delivery plans.' 
                  : 'Get in touch to discuss enrolment and funding options.'}
              </p>
              <Link to="/contact" className="sidebar-v2__button">
                Enquire now
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 10h12M12 4l6 6-6 6" />
                </svg>
              </Link>
            </div>
            
            {course.careerPaths && course.careerPaths.length > 0 && !course.isBusiness && (
              <div className="sidebar-v2">
                <h3 className="sidebar-v2__title">Career paths</h3>
                <div className="careers-v2">
                  {course.careerPaths.map((path, i) => (
                    <div key={i} className="careers-v2__item">
                      <span className="careers-v2__sector">{path.title}</span>
                      <span className="careers-v2__roles">{path.roles}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {course.progressionOpportunities && course.progressionOpportunities.length > 0 && (
              <div className="sidebar-v2">
                <h3 className="sidebar-v2__title">What's next</h3>
                <ul className="progression-v2">
                  {course.progressionOpportunities.map((opp, i) => (
                    <li key={i} className="progression-v2__item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M8 2v12M2 8l6 6 6-6" />
                      </svg>
                      {opp}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {course.courseBenefits && course.courseBenefits.length > 0 && !course.isBusiness && (
              <div className="sidebar-v2">
                <h3 className="sidebar-v2__title">Benefits</h3>
                <ul className="benefits-v2">
                  {course.courseBenefits.map((benefit, i) => (
                    <li key={i} className="benefits-v2__item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 4.5L6 10.5 4 8.5" />
                      </svg>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {course.isBusiness && course.courseBenefits && course.courseBenefits.length > 0 && (
              <div className="sidebar-v2">
                <h3 className="sidebar-v2__title">Package Features</h3>
                <ul className="benefits-v2">
                  {course.courseBenefits.map((benefit, i) => (
                    <li key={i} className="benefits-v2__item">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 4.5L6 10.5 4 8.5" />
                      </svg>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {course.deliveryMethods && course.deliveryMethods.length > 0 && (
              <div className="sidebar-v2">
                <h3 className="sidebar-v2__title">Delivery</h3>
                <div className="delivery-v2">
                  {course.deliveryMethods.map((method, i) => (
                    <span key={i} className="delivery-v2__tag">{method}</span>
                  ))}
                </div>
              </div>
            )}
            
            {course.enrollmentInfo && !course.isBusiness && (
              <div className="sidebar-v2">
                <h3 className="sidebar-v2__title">Enrolment</h3>
                <div className="enrolment-v2">
                  {course.enrollmentInfo.nextIntake && (
                    <div className="enrolment-v2__row">
                      <span className="enrolment-v2__label">Next intake</span>
                      <span className="enrolment-v2__value">{course.enrollmentInfo.nextIntake}</span>
                    </div>
                  )}
                  {course.enrollmentInfo.schedule && (
                    <div className="enrolment-v2__row">
                      <span className="enrolment-v2__label">Schedule</span>
                      <span className="enrolment-v2__value">{course.enrollmentInfo.schedule}</span>
                    </div>
                  )}
                  {course.enrollmentInfo.support && (
                    <div className="enrolment-v2__row">
                      <span className="enrolment-v2__label">Support</span>
                      <span className="enrolment-v2__value">{course.enrollmentInfo.support}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
          </aside>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CoursePage;